import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { buildPublicUrl, getBackendUrl } from '../../services/origin';
import { apiGet } from '../../services/publicApi';
import { Search, Download, ChevronLeft, ChevronRight, Filter, Users, UserX, UserPlus, Trophy, X, TrendingUp, TrendingDown, Repeat, Zap, Plus } from 'lucide-react';
import TierBadge from '../../staff/components/TierBadge';
import { PageTooltip } from '../components/TooltipSystem';
import { useBranding } from '../../context/BrandingContext';

const TIER_KEYS = ['bronze', 'silver', 'gold', 'platinum'];

const SEGMENTS = [
  { value: '', label: 'Tutti', icon: Users },
  { value: 'inactive', label: 'Inattivi', icon: UserX },
  { value: 'new', label: 'Nuovi', icon: UserPlus },
  { value: 'top_month', label: 'Top del mese', icon: Trophy },
];

const SPENDING_SEGMENTS = [
  { value: '', label: 'Tutti' },
  { value: 'high_spender', label: 'Alto spendente', icon: TrendingUp },
  { value: 'low_spender', label: 'Basso spendente', icon: TrendingDown },
];

const FREQUENCY_SEGMENTS = [
  { value: '', label: 'Tutti' },
  { value: 'high_frequency', label: 'Alta frequenza', icon: Zap },
  { value: 'recurring', label: 'Ricorrente', icon: Repeat },
];

const CHANNEL_MAP = {
  passaparola: 'Passaparola',
  social: 'Social',
  volantino: 'Volantino',
  google: 'Google',
  evento: 'Evento',
  passaggio: 'Passaggio',
  altro: 'Altro',
};

export default function AdminCustomers() {
  const { tier_names } = useBranding();
  const TIERS = TIER_KEYS.map(k => ({
    value: k,
    label: tier_names?.[k] || (k ? k[0].toUpperCase() + k.slice(1) : k),
  }));
  const [data, setData] = useState({ customers: [], total: 0, page: 1, pages: 1 });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();

  // Advanced filters
  const [segment, setSegment] = useState('');
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [minPoints, setMinPoints] = useState('');
  const [minVisits, setMinVisits] = useState('');
  const [inactiveDays, setInactiveDays] = useState('30');
  const [newDays, setNewDays] = useState('7');
  const [spendingSegment, setSpendingSegment] = useState('');
  const [frequencySegment, setFrequencySegment] = useState('');
  const [customerType, setCustomerType] = useState(''); // '' | 'member' | 'prospect'

  const hasActiveFilters = segment || selectedTiers.length > 0 || minPoints || minVisits || spendingSegment || frequencySegment || customerType;

  const buildParams = useCallback((p = 1, s = search) => {
    const params = new URLSearchParams();
    params.set('page', p);
    if (s) params.set('search', s);
    if (segment) {
      params.set('segment', segment);
      if (segment === 'inactive') params.set('inactive_days', inactiveDays || '30');
      if (segment === 'new') params.set('new_days', newDays || '7');
    }
    if (selectedTiers.length > 0) params.set('tiers', selectedTiers.join(','));
    if (minPoints) params.set('min_points', minPoints);
    if (minVisits) params.set('min_visits', minVisits);
    if (spendingSegment) params.set('spending_segment', spendingSegment);
    if (frequencySegment) params.set('frequency_segment', frequencySegment);
    if (customerType) params.set('customer_type', customerType);
    return params.toString();
  }, [search, segment, selectedTiers, minPoints, minVisits, inactiveDays, newDays, spendingSegment, frequencySegment, customerType]);

  const fetchCustomers = useCallback((p = 1, s = search) => {
    setLoading(true);
    const qs = buildParams(p, s);
    adminApi.get(`/admin/customers?${qs}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento clienti'))
      .finally(() => setLoading(false));
  }, [buildParams, search]);

  useEffect(() => { fetchCustomers(1, ''); }, []);
  // Re-fetch automatically when the customer-type tab changes
  useEffect(() => {
    fetchCustomers(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerType]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCustomers(1, search);
  };

  const applyFilters = () => {
    setPage(1);
    fetchCustomers(1);
  };

  const clearFilters = () => {
    setSegment('');
    setSelectedTiers([]);
    setMinPoints('');
    setMinVisits('');
    setInactiveDays('30');
    setNewDays('7');
    setSpendingSegment('');
    setFrequencySegment('');
    setCustomerType('');
    setPage(1);
    // Fetch with empty filters
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', '1');
    if (search) params.set('search', search);
    adminApi.get(`/admin/customers?${params.toString()}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento clienti'))
      .finally(() => setLoading(false));
  };

  const toggleTier = (tier) => {
    setSelectedTiers(prev =>
      prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]
    );
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const qs = buildParams(1);
      const res = await apiGet(`/api/admin/export/customers?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      if (res.status < 200 || res.status >= 300) throw new Error('export failed');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'clienti.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export completato!');
    } catch { toast.error("Errore durante l'export"); }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10">
      <PageTooltip page="customers" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Clienti</h1>
          <p className="text-[#64748B] text-sm">{data.total} clienti{hasActiveFilters ? ' (filtrati)' : ' registrati'}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
            className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-xl text-sm font-medium transition-colors ${
              hasActiveFilters
                ? 'bg-[#D44E20] text-white border-[#D44E20]'
                : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'
            }`}
          >
            <Filter size={16} /> Filtri{hasActiveFilters ? ` (${[segment, selectedTiers.length > 0 && 'tier', minPoints && 'pt', minVisits && 'vis'].filter(Boolean).length})` : ''}
          </button>
          <button onClick={handleExport} data-testid="export-btn" className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-200 text-[#64748B] rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
            <Download size={16} /> Esporta CSV
          </button>
          <button onClick={() => setShowAddModal(true)} data-testid="add-customer-btn" className="flex items-center justify-center gap-2 px-4 py-2 bg-[#D44E20] hover:bg-[#b8411a] text-white rounded-xl text-sm font-semibold transition-colors">
            <Plus size={16} /> Aggiungi cliente
          </button>
        </div>
      </div>

      {showAddModal && (
        <AddCustomerModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchCustomers(); }}
        />
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome o telefono..."
            data-testid="customer-search"
            className="w-full h-10 pl-9 pr-4 bg-white border border-slate-200 text-[#1A1A1A] rounded-xl text-sm focus:border-[#D44E20] focus:outline-none"
          />
        </div>
        <button type="submit" className="px-4 h-10 bg-[#D44E20] text-white rounded-xl text-sm font-medium hover:bg-[#B83D15] transition-colors">Cerca</button>
      </form>

      {/* Customer-type tabs — segments members (loyalty enrolled) from
          prospects (online buyers who never opted in). */}
      <div className="flex gap-1 mb-4 bg-white rounded-xl border border-slate-100 p-1 shadow-sm" data-testid="customer-type-tabs">
        {[
          { value: '', label: 'Tutti' },
          { value: 'member', label: 'Iscritti' },
          { value: 'prospect', label: 'Acquirenti senza iscrizione' },
        ].map(opt => (
          <button
            key={opt.value || 'all'}
            onClick={() => { setCustomerType(opt.value); setPage(1); }}
            data-testid={`customer-type-tab-${opt.value || 'all'}`}
            className={`flex-1 h-9 px-3 rounded-lg text-xs font-bold transition-colors ${
              customerType === opt.value
                ? 'bg-[#D44E20] text-white'
                : 'text-[#64748B] hover:bg-slate-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mb-4 animate-fade-in-up" data-testid="filters-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1A1A1A] text-sm flex items-center gap-2"><Filter size={14} /> Filtri avanzati</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} data-testid="clear-filters-btn" className="text-xs text-[#64748B] hover:text-[#D44E20] flex items-center gap-1">
                <X size={12} /> Resetta filtri
              </button>
            )}
          </div>

          {/* Segment */}
          <div className="mb-4">
            <label className="text-xs text-[#64748B] font-medium block mb-2">Segmento</label>
            <div className="flex gap-2 flex-wrap">
              {SEGMENTS.map(s => (
                <button
                  key={s.value}
                  onClick={() => setSegment(s.value)}
                  data-testid={`filter-segment-${s.value || 'all'}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                    segment === s.value
                      ? 'bg-[#D44E20] text-white border-[#D44E20]'
                      : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <s.icon size={13} /> {s.label}
                </button>
              ))}
            </div>
            {segment === 'inactive' && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-[#64748B]">Inattivi da</span>
                <input type="number" value={inactiveDays} onChange={e => setInactiveDays(e.target.value)} data-testid="filter-inactive-days"
                  className="w-16 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-center" />
                <span className="text-xs text-[#64748B]">giorni</span>
              </div>
            )}
            {segment === 'new' && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-[#64748B]">Iscritti negli ultimi</span>
                <input type="number" value={newDays} onChange={e => setNewDays(e.target.value)} data-testid="filter-new-days"
                  className="w-16 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2 text-xs text-center" />
                <span className="text-xs text-[#64748B]">giorni</span>
              </div>
            )}
          </div>

          {/* Spending Segment */}
          <div className="mb-4">
            <label className="text-xs text-[#64748B] font-medium block mb-2">Segmento spesa</label>
            <div className="flex gap-2 flex-wrap">
              {SPENDING_SEGMENTS.map(s => (
                <button key={s.value} onClick={() => setSpendingSegment(s.value)} data-testid={`filter-spending-${s.value || 'all'}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${spendingSegment === s.value ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'}`}>
                  {s.icon && <s.icon size={13} />} {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Frequency Segment */}
          <div className="mb-4">
            <label className="text-xs text-[#64748B] font-medium block mb-2">Segmento frequenza</label>
            <div className="flex gap-2 flex-wrap">
              {FREQUENCY_SEGMENTS.map(s => (
                <button key={s.value} onClick={() => setFrequencySegment(s.value)} data-testid={`filter-frequency-${s.value || 'all'}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${frequencySegment === s.value ? 'bg-[#D44E20] text-white border-[#D44E20]' : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'}`}>
                  {s.icon && <s.icon size={13} />} {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tiers multi-select */}
          <div className="mb-4">
            <label className="text-xs text-[#64748B] font-medium block mb-2">Tier (multi-selezione)</label>
            <div className="flex gap-2 flex-wrap">
              {TIERS.map(t => (
                <button
                  key={t.value}
                  onClick={() => toggleTier(t.value)}
                  data-testid={`filter-tier-${t.value}`}
                  className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                    selectedTiers.includes(t.value)
                      ? 'bg-[#D44E20] text-white border-[#D44E20]'
                      : 'bg-white text-[#64748B] border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Min points / visits */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-[#64748B] font-medium block mb-1.5">Punti minimi</label>
              <input type="number" value={minPoints} onChange={e => setMinPoints(e.target.value)} placeholder="Es. 100" data-testid="filter-min-points"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] font-medium block mb-1.5">Visite minime</label>
              <input type="number" value={minVisits} onChange={e => setMinVisits(e.target.value)} placeholder="Es. 5" data-testid="filter-min-visits"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>

          {/* Apply */}
          <button
            onClick={applyFilters}
            data-testid="apply-filters-btn"
            className="w-full h-10 bg-[#D44E20] text-white rounded-xl text-sm font-bold hover:bg-[#B83D15] transition-colors"
          >
            Applica filtri
          </button>
        </div>
      )}

      {/* Customer List */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-slate-100">
                <tr>
                  {['Nome', 'Telefono', 'Tier', 'Punti', 'Visite', 'Canale', 'Campagna', 'Ultima visita'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.customers.map(c => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/admin/customers/${c.id}`)}
                    data-testid={`customer-row-${c.id}`}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#D44E20]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-[#D44E20] font-bold text-xs">{c.name?.charAt(0)}</span>
                        </div>
                        <span className="text-[#1A1A1A] font-medium text-sm">{c.name}</span>
                        {c.customer_type === 'prospect' && (
                          <span data-testid={`prospect-badge-row-${c.id}`} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Non iscritto</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#64748B] text-sm">{c.phone}</td>
                    <td className="px-4 py-3">{c.customer_type === 'prospect' ? <span className="text-[#bbb] text-xs">—</span> : <TierBadge tier={c.tier} />}</td>
                    <td className="px-4 py-3 text-[#D44E20] font-bold text-sm tabular-nums">{c.points?.toLocaleString('it-IT')}</td>
                    <td className="px-4 py-3 text-[#64748B] text-sm tabular-nums">{c.total_visits}</td>
                    <td className="px-4 py-3 text-[#64748B] text-xs">{CHANNEL_MAP[c.acquisition_channel] || <span className="text-[#bbb]">—</span>}</td>
                    <td className="px-4 py-3 text-[#64748B] text-xs truncate max-w-[120px]">{c.campaign_name || <span className="text-[#bbb]">—</span>}</td>
                    <td className="px-4 py-3 text-[#64748B] text-xs">{c.last_visit_at ? new Date(c.last_visit_at).toLocaleDateString('it-IT') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-slate-100">
              {data.customers.map(c => (
                <div
                  key={c.id}
                  onClick={() => navigate(`/admin/customers/${c.id}`)}
                  data-testid={`customer-card-${c.id}`}
                  className="p-4 active:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D44E20]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D44E20] font-bold text-sm">{c.name?.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[#1A1A1A] font-semibold text-sm truncate">{c.name}</span>
                        {c.customer_type === 'prospect' ? (
                          <span data-testid={`prospect-badge-${c.id}`} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 flex-shrink-0">Non iscritto</span>
                        ) : (
                          <TierBadge tier={c.tier} />
                        )}
                      </div>
                      <p className="text-[#64748B] text-xs mt-0.5">{c.phone}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[#D44E20] font-bold text-sm tabular-nums">{c.points?.toLocaleString('it-IT')} pt</p>
                      <p className="text-[#64748B] text-[10px] tabular-nums">{c.total_visits} visite</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 ml-[52px] flex-wrap">
                    {c.acquisition_channel && <span className="text-[10px] text-[#8B5CF6] bg-[#8B5CF6]/8 px-1.5 py-0.5 rounded">{CHANNEL_MAP[c.acquisition_channel] || c.acquisition_channel}</span>}
                    {c.campaign_name && <span className="text-[10px] text-[#F59E0B] bg-[#F59E0B]/8 px-1.5 py-0.5 rounded">{c.campaign_name}</span>}
                    {c.last_visit_at && <span className="text-[10px] text-[#64748B]">{new Date(c.last_visit_at).toLocaleDateString('it-IT')}</span>}
                  </div>
                </div>
              ))}
            </div>

            {data.customers.length === 0 && (
              <div className="text-center py-12 text-[#64748B]">Nessun cliente trovato</div>
            )}
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <p className="text-[#64748B] text-xs">Pagina {data.page} di {data.pages} ({data.total} risultati)</p>
              <div className="flex gap-2">
                <button onClick={() => { const p = page - 1; setPage(p); fetchCustomers(p); }} disabled={page <= 1} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-[#64748B] hover:bg-slate-50 disabled:opacity-40 transition-colors">
                  <ChevronLeft size={16} />
                </button>
                <button onClick={() => { const p = page + 1; setPage(p); fetchCustomers(p); }} disabled={page >= data.pages} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-[#64748B] hover:bg-slate-50 disabled:opacity-40 transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function AddCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', birthday: '', acquisition_channel: '', campaign_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [consentUrl, setConsentUrl] = useState('');
  const [activeCampaigns, setActiveCampaigns] = useState([]);

  useEffect(() => {
    adminApi.get('/campaigns/active')
      .then(r => setActiveCampaigns(r.data.campaigns || []))
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Nome e telefono sono obbligatori');
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminApi.post('/customers', {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        birthday: form.birthday || null,
        acquisition_channel: form.acquisition_channel || '',
        campaign_id: form.campaign_id || null,
        privacy_accepted: true,
      });
      toast.success(`Cliente "${form.name}" creato`);
      // If backend returned a consent_url (phone needs onboarding) keep modal open showing it.
      // Always rebuild from current page origin so the link follows the
      // domain the admin is browsing (host-agnostic).
      if (res.data?.consent_token) {
        setConsentUrl(buildPublicUrl(`/consent/${res.data.consent_token}`));
      } else {
        onCreated();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione cliente');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="add-customer-modal">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#1A1A1A]">Aggiungi cliente</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>

        {consentUrl ? (
          <div className="space-y-3">
            <p className="text-sm text-emerald-700 font-semibold">✓ Cliente creato</p>
            <p className="text-xs text-[#64748B]">Condividi questo link con il cliente per completare l'onboarding (privacy + WhatsApp).</p>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-2 text-[11px] font-mono break-all" data-testid="add-customer-consent-url">{consentUrl}</div>
            <button onClick={onCreated} data-testid="add-customer-done-btn" className="w-full h-11 rounded-xl bg-[#D44E20] text-white font-semibold text-sm">Chiudi</button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <Field label="Nome *" value={form.name} onChange={v => setForm(f => ({...f, name: v}))} testid="new-customer-name" />
              <Field label="Telefono *" value={form.phone} onChange={v => setForm(f => ({...f, phone: v}))} testid="new-customer-phone" placeholder="+39 333 1234567" />
              <Field label="Email" value={form.email} onChange={v => setForm(f => ({...f, email: v}))} testid="new-customer-email" />
              <Field label="Data di nascita" value={form.birthday} onChange={v => setForm(f => ({...f, birthday: v}))} testid="new-customer-birthday" type="date" />
              <div>
                <label className="text-xs font-medium text-[#64748B] block mb-1">Canale acquisizione</label>
                <select value={form.acquisition_channel} onChange={e => setForm(f => ({...f, acquisition_channel: e.target.value}))} data-testid="new-customer-channel" className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:border-[#D44E20] focus:outline-none">
                  <option value="">Non specificato</option>
                  {Object.entries(CHANNEL_MAP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#64748B] block mb-1">Campagna attiva</label>
                {activeCampaigns.length > 0 ? (
                  <select value={form.campaign_id} onChange={e => setForm(f => ({...f, campaign_id: e.target.value}))} data-testid="new-customer-campaign" className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:border-[#D44E20] focus:outline-none">
                    <option value="">Nessuna campagna</option>
                    {activeCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <p className="text-xs italic text-slate-400">Nessuna campagna attiva al momento</p>
                )}
                <p className="text-[10px] text-slate-400 mt-1">Il premio verrà assegnato in automatico dopo la firma del consenso privacy.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 text-[#1A1A1A] text-sm font-medium hover:bg-slate-50">Annulla</button>
              <button onClick={submit} disabled={submitting} data-testid="submit-add-customer-btn" className="flex-1 h-11 rounded-xl bg-[#D44E20] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#b8411a]">
                {submitting ? 'Creazione...' : 'Crea cliente'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#64748B] block mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm focus:border-[#D44E20] focus:outline-none"
      />
    </div>
  );
}
